import { useEffect, useState } from "react";
import { api } from "./api";

const INTERVAL_MS = 15000;

/**
 * How many other people have this product open, and whether anyone has just
 * added it to their cart.
 *
 * The numbers are real. Shops routinely invent them — "12 people are viewing
 * this" is usually a random number on a timer — and that is a lie told to hurry
 * a customer along. This counts open pages, so most of the time it counts none,
 * and the page shows nothing. When it does say someone else is here, someone
 * else is here.
 *
 * The id identifies a tab, not a person: it is random, lives in sessionStorage
 * and disappears when the tab closes. The visitor is excluded from their own
 * counts.
 */
export const sessionId = () => {
  const key = "3duo-presence";
  let id = sessionStorage.getItem(key);
  if (!id) {
    id = Math.random().toString(36).slice(2) + Date.now().toString(36);
    sessionStorage.setItem(key, id);
  }
  return id;
};

/**
 * Report that this product went into a cart.
 *
 * Called from the cart itself rather than from a button, because there are
 * three ways to add something - the product page, the "+" on a card, and the
 * dialog that asks which model - and only the cart sees all of them. Wiring it
 * to a button also announced additions that had not happened yet: on a product
 * with models, the button opens a dialog that can still be cancelled.
 */
export const announceCartAdd = (productId) => {
  if (!productId) return;
  api.post("/presence", {
    product_id: productId,
    session: sessionId(),
    added_to_cart: true,
  }).catch(() => {});
};

export const usePresence = (productId) => {
  const [presence, setPresence] = useState({ viewers: 0, cart_adds: 0, orders: 0 });

  useEffect(() => {
    if (!productId) return undefined;

    let cancelled = false;
    const session = sessionId();

    const ping = async () => {
      try {
        const { data } = await api.post("/presence", { product_id: productId, session });
        if (!cancelled) setPresence(data);
      } catch {
        // Silent on purpose: this decorates the page and must never
        // interrupt someone trying to buy something.
      }
    };

    ping();
    const timer = setInterval(ping, INTERVAL_MS);
    return () => { cancelled = true; clearInterval(timer); };
  }, [productId]);

  return presence;
};
